import { LoadingOverlay } from 'components/UI/LoadingOverlay/LoadingOverlay';
import { push } from 'connected-react-router';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import { dispatch } from 'store';
import { initOnlineBookingThunk } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingWizard } from 'components/OnlineBooking/OnlineBookingWizard';
import { getOnlineBookingState } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { useIsOnlineBookingEnabled } from 'hooks/useIsOnlineBookingEnabled';
import { OnlineBookingDisabled } from 'components/OnlineBooking/OnlineBookingDisabled/OnlineBookingDisabled';
import { redeemHandoffCodeThunk } from 'actions/handoff/HandoffActions';
import { applyBrandColors } from 'utils/brandColors';
import { AddonsModeError } from 'components/AddonsMode/AddonsModeError';
import { AddonsModeFlow } from 'components/AddonsMode/AddonsModeFlow';
import { takeAddonsModeEntry, takeHandoffCode } from 'utils/petParentPortal';

// Captured as the module loads, so the code leaves the address bar immediately
// and StrictMode's double effect cannot spend the single-use code twice.
let pendingHandoffCode = takeHandoffCode();
// Set when the portal sent the pet parent to add Add-Ons to an existing booking.
const addonsModeEntry = takeAddonsModeEntry(pendingHandoffCode !== null);

export const OnlineBookingPage: React.FC = () => {
    const queryParams = useParams<{ slug: string }>();

    const { isLoading } = useSelector(getOnlineBookingState);
    const { id: marketplaceId, slug: marketplaceSlug, settings } = useSelector(getMarketplace);
    const isOnlineBookingEnabled = useIsOnlineBookingEnabled();

    const [isRedeemingHandoff, setIsRedeemingHandoff] = React.useState(pendingHandoffCode !== null);
    const [handoffFailed, setHandoffFailed] = React.useState(false);

    React.useEffect(() => {
        if (marketplaceId === 0 || marketplaceSlug !== queryParams.slug) {
            dispatch(initOnlineBookingThunk(queryParams.slug)).catch(() => {
                // Redirect to 404 page
                dispatch(push('/'));
            });
        }
    }, [marketplaceId, marketplaceSlug, queryParams.slug]);

    React.useEffect(() => {
        const handoffCode = pendingHandoffCode;

        if (!handoffCode || marketplaceId === 0 || marketplaceSlug !== queryParams.slug) {
            return;
        }

        pendingHandoffCode = null;

        dispatch(
            redeemHandoffCodeThunk(handoffCode, marketplaceId, {
                startWizard: addonsModeEntry === null
            })
        )
            .then(redeemed => setHandoffFailed(!redeemed))
            .finally(() => setIsRedeemingHandoff(false));
    }, [marketplaceId, marketplaceSlug, queryParams.slug]);

    React.useEffect(() => {
        applyBrandColors(settings);
    }, [settings]);

    if (isLoading || isRedeemingHandoff) {
        return <LoadingOverlay />;
    }

    // Add-ons mode is its own flow, only for the portal: it skips the online booking gate and
    // never shows the public flow. After a reload the session is gone, so it can only go back.
    if (addonsModeEntry !== null) {
        // Wait for the marketplace: the flow reads its id, catalog and branding.
        if (marketplaceId === 0) {
            return <LoadingOverlay />;
        }

        return handoffFailed || addonsModeEntry.restored ? (
            <AddonsModeError
                bookingId={addonsModeEntry.bookingId}
                title="Your session expired"
                message="Open Add Add-Ons again from your booking in the Pet Parent Portal."
            />
        ) : (
            <AddonsModeFlow bookingId={addonsModeEntry.bookingId} />
        );
    }

    if (!isOnlineBookingEnabled) {
        return <OnlineBookingDisabled />;
    }

    return <OnlineBookingWizard />;
};
